import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';

interface ServiceWorkerUpdate {
  waiting: ServiceWorker | null;
  registration: ServiceWorkerRegistration;
}

const UpdatePrompt: React.FC = () => {
  const [updateAvailable, setUpdateAvailable] = useState<ServiceWorkerUpdate | null>(
    null
  );
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    // Check if service worker is supported
    if ('serviceWorker' in navigator) {
      // Watch for service worker updates
      navigator.serviceWorker.ready.then((registration) => {
        registration.addEventListener('controllerchange', () => {
          // When the service worker is updated, reload the page
          window.location.reload();
        });
      });

      // Check for updates
      const checkForUpdates = () => {
        navigator.serviceWorker.getRegistration().then((registration) => {
          if (registration) {
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (
                    newWorker.state === 'installed' &&
                    navigator.serviceWorker.controller
                  ) {
                    setUpdateAvailable({ waiting: newWorker, registration });
                  }
                });
              }
            });
          }
        });
      };

      // Check immediately and then every 60 minutes
      checkForUpdates();
      const interval = setInterval(checkForUpdates, 60 * 60 * 1000);

      return () => clearInterval(interval);
    }
  }, []);

  const handleUpdate = async () => {
    if (!updateAvailable?.waiting) return;

    try {
      setUpdating(true);

      // Send message to service worker to skip waiting
      updateAvailable.waiting.postMessage({ type: 'SKIP_WAITING' });

      // Force reload after a short delay to ensure the new service worker is active
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error updating PWA:', error);
      setUpdating(false);
    }
  };

  const dismissUpdate = () => {
    setUpdateAvailable(null);
  };

  if (!updateAvailable) return null;

  return (
    <div className="fixed top-20 left-0 right-0 mx-auto px-4 z-50 max-w-lg">
      <Alert className="bg-primary backdrop-blur supports-[backdrop-filter]:bg-primary/75 text-primary-foreground shadow-lg border-0">
        <div className="flex items-start gap-3">
          <div className="bg-primary-foreground/10 p-2 rounded-md">
            <RefreshCw className={`h-4 w-4 ${updating ? 'animate-spin' : ''}`} />
          </div>
          <div className="flex-1 min-w-0">
            <AlertTitle className="text-sm font-semibold mb-1">
              Update Available
            </AlertTitle>
            <AlertDescription className="text-sm text-primary-foreground/90">
              A new version is available. Update now for the latest features.
            </AlertDescription>
          </div>
          <div className="flex items-center gap-2 ml-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={updating}
              className="text-xs px-3 h-8 shadow-sm hover:shadow"
              onClick={handleUpdate}
            >
              <RefreshCw className={`mr-1 h-3 w-3 ${updating ? 'animate-spin' : ''}`} />
              {updating ? 'Updating...' : 'Update'}
            </Button>
            {!updating && (
              <Button
                variant="ghost"
                size="sm"
                onClick={dismissUpdate}
                className="text-xs h-8 px-2 hover:bg-primary-foreground/10"
              >
                ✕
              </Button>
            )}
          </div>
        </div>
      </Alert>
    </div>
  );
};

export default UpdatePrompt;
